/**
 * TYPES
 */
import {AckTypes} from 'src/aggregates/conversations/message/messages.d';
import {ITemplateInput} from 'src/aggregates/conversations/queries.d';
import {_aggregates} from 'src/aggregates/index.d';
import {ITimelineAdd} from 'src/aggregates/timelines/commands.d';
import {ITimelineClearByConversation}
    from 'src/aggregates/timelines/commands.d';
import {ITimelineFetch} from 'src/aggregates/timelines/commands.d';
import {ITimelineFetchMessageByOriginalId}
    from 'src/aggregates/timelines/commands.d';
import {ITimelineFocus} from 'src/aggregates/timelines/commands.d';
import {ITimelineLoadByContact} from 'src/aggregates/timelines/commands.d';
import {ITimelineLoadNextConversationMessages}
    from 'src/aggregates/timelines/commands.d';
import {ITimelineLoadNextMessagesByConversation}
    from 'src/aggregates/timelines/commands.d';
import {ITimelineOnMessageAck} from 'src/aggregates/timelines/commands.d';
import {ITimelineRemove} from 'src/aggregates/timelines/commands.d';
import {ITimelineResetError} from 'src/aggregates/timelines/commands.d';
import {ITimelineSendTemplate} from 'src/aggregates/timelines/commands.d';
import {_names} from 'src/aggregates/timelines/commands.d';
import {IMessage} from 'src/aggregates/timelines/state.d';


/**
 * CODE
 */

/**
 * I am a command creator for timeline add.
 *
 * :param account: account id
 * :param application: conversation current application uid
 * :param contact: contact id of conversation being added
 * :param end: conversation end timestamp
 * :param id: conversation id
 * :param lastInteraction: conversation last interaction timestamp
 * :param messages: timeline event messages
 * :param remote: contact uid
 * :param start: conversation start timestamp
 * :param status: conversation exit status
 * :param subject: conversation subject
 *
 * :returns: timeline add command
 */
function add (
    account: number,
    application: string | null,
    contact: number | string,
    end: number | null,
    id: number,
    lastInteraction: number,
    messages: IMessage[],
    remote: string,
    start: number,
    status: number | null,
    subject: string | null,
): ITimelineAdd
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_ADD,
        _target: null,
        account,
        application,
        contact,
        end,
        id,
        lastInteraction,
        messages,
        remote,
        start,
        status,
        subject,
    };
}


/*
 * I am a command creator for timeline clear by conversation command.
 *
 * :returns: timeline clear by conversation command
 */
function clearByConversation (): ITimelineClearByConversation
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_CLEAR_BY_CONVERSATION,
        _target: null,
    };
}


/*
 * I am a command creator for fetch messages.
 *
 * :param id: conversation id
 *
 * :returns: timeline fetch command
 */
function fetch (id: number): ITimelineFetch
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_FETCH,
        _target: id,
    };
}


/**
 * I am a command creator for fetch message by original id.
 *
 * :param message: message id
 * :param reply: original id of message being replied
 * :param conversation: conversation id of replying message
 *
 * :returns: fetch message by original id command
 */
function fetchMessageByOriginalId (
    message: number,
    reply: string,
    conversation: number,
): ITimelineFetchMessageByOriginalId
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_FETCH_MESSAGE_BY_ORIGINAL_ID,
        _target: conversation,
        message,
        reply,
    };
}


/*
 * I am a command creator for timeline focus.
 *
 * :param id: conversation id
 *
 * :returns: timeline focus command
 */
function focus (id: number): ITimelineFocus
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_FOCUS,
        _target: id,
    };
}


/**
 * I am a command creator for timeline load by contact.
 *
 * :param contact: contact id
 *
 * :returns: timeline load by contact command
 */
function loadByContact (contact: number | string): ITimelineLoadByContact
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_LOAD_BY_CONTACT,
        _target: null,
        contact,
    };
}


/**
 * I am a command creator for timeline load by customer.
 *
 * :returns: timeline load messages command
 */
function loadNextConversationMessages (): ITimelineLoadNextConversationMessages
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_LOAD_NEXT_CONVERSATION_MESSAGES,
        _target: null,
    };
}


/**
 * I am a command creator for timeline load next messages by conversation.
 *
 * :param conversation: conversation id
 *
 * :returns: timeline next messages by conversation command
 */
function loadNextMessagesByConversation (
    conversation: number,
): ITimelineLoadNextMessagesByConversation
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_LOAD_NEXT_MESSAGES_BY_CONVERSATION,
        _target: null,
        conversation,
    };
}


/**
 * I am a timeline command creator for on message ack.
 *
 * :param conversationId: conversation id
 * :param messageId: message id
 * :param ack: message ack
 * :param originalId: message originalId
 *
 * :returns: timeline conversation on message ack command
 */
function onMessageAck (
    conversationId: number,
    messageId: number,
    ack: AckTypes,
    originalId: string,
): ITimelineOnMessageAck
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_ON_MESSAGE_ACK,
        _target: conversationId,
        ack,
        id: messageId,
        originalId,
    };
}


/**
 * I am a command creator for timeline remove.
 *
 * :param id: conversation id
 *
 * :returns: timeline remove command
 */
function remove (id: number): ITimelineRemove
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_REMOVE,
        _target: id,
    };
}


/**
 * I am a command creator for timeline reset error.
 *
 * :returns: timeline reset error command
 */
function resetError (): ITimelineResetError
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_RESET_ERROR,
        _target: null,
    };
}


/**
 * I am a command creator for timeline send template.
 *
 * :param id: conversation id
 * :param template: template to be sent
 *
 * :returns: timeline remove command
 */
function sendTemplate(
    id: number,
    templateInput: ITemplateInput,
): ITimelineSendTemplate
{
    return {
        _aggregate: _aggregates.TIMELINES,
        _name: _names.TIMELINE_SEND_TEMPLATE,
        _target: id,
        templateInput,
    };
}


/**
 * EXPORTS
 */
export {
    add,
    clearByConversation,
    fetch,
    fetchMessageByOriginalId,
    focus,
    loadByContact,
    loadNextConversationMessages,
    loadNextMessagesByConversation,
    onMessageAck,
    remove,
    resetError,
    sendTemplate,
};
